/* eslint-disable import/no-cycle */
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  ComponentNameEnum,
  ElementPartTypeEnum,
  StepperSignEnum,
  TemplateStructureEnum,
  AlignValueEnum,
} from '@/types';
import getHorizontalSpacesFieldConfig from '@/components/template-builder/config/fields/horizontal-spaces-config';
import getVerticalSpacesFieldConfig from '@/components/template-builder/config/fields/vertical-spaces-config';
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
import getBorderRadiusFieldConfig from '@/components/template-builder/config/fields/border-radius-config';
import mediaDisplayFieldConfig from '@/components/template-builder/config/fields/media-display-config';
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
import getElementAlignFieldConfig from '@/components/template-builder/config/fields/element-align-config';
import { getTimezoneOptions } from '@/components/template-builder/config/fields/timezone-options';
import getCountdownDateVariableOptions from '@/components/template-builder/config/fields/countdown-date-variables';
import { UserState } from '@/composables/User';
import {
  setCssProperty,
  setCssPropertyAndRemoveIfZero,
  setMediaDisplay,
  setAttribute,
  setEmailCountdownEndDate,
  setEmailCountdownDateVariable,
  setEmailCountdownTimezone,
  setEmailCountdownStyle,
  setEmailCountdownLabel,
  setEmailCountdownShowDays,
  setEmailCountdownShowHours,
  setEmailCountdownShowMinutes,
  setEmailCountdownShowSeconds,
} from '@/components/template-builder/setters';
import {
  getClassByName,
  getCssPropertyByName,
  getAttributeByName,
} from '@/components/template-builder/getters';
import getConditionsAndRepetitionGroup from '@/components/template-builder/config/fields/conditions-repetition-group-helper';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import { HIDE_MOBILE_ATTRIBUTE } from '@/components/template-builder/utils/constants';
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';

const emailWidgetCountdownConfig: WidgetTypeConfig = {
  tabPanel: {
    label: 'templateBuilder.widgetsHeaders.countdown',
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.widgetConfiguration',
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownSettings',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.countdownDateType',
                  selector: '{SELECTOR}',
                  fieldId: 'countdownDateType',
                  properties: [{
                    name: 'data-date-type',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                    refreshWidgetFields: true,
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: 'fixed',
                    list: [
                      { value: 'fixed', label: 'templateBuilder.fields.countdownDateTypeFixed' },
                      { value: 'variable', label: 'templateBuilder.fields.countdownDateTypeVariable' },
                    ],
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownEndDate',
                  selector: '{SELECTOR}',
                  parentFieldId: 'countdownDateType',
                  isVisible: (items: any[], parentFieldId: string) => {
                    const property = items.find((item) => item.fieldId === parentFieldId);
                    if (property && Array.isArray(property.properties) && property.properties.length) {
                      return property.properties[0].value === 'fixed';
                    }
                    return true;
                  },
                  properties: [{
                    name: 'data-end-date',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownEndDate],
                  }],
                  componentName: ComponentNameEnum.DATETIME_PICKER,
                  options: {
                    defaultValue: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownTimezone',
                  selector: '{SELECTOR}',
                  parentFieldId: 'countdownDateType',
                  isVisible: (items: any[], parentFieldId: string) => {
                    const property = items.find((item) => item.fieldId === parentFieldId);
                    if (property && Array.isArray(property.properties) && property.properties.length) {
                      return property.properties[0].value === 'fixed';
                    }
                    return true;
                  },
                  properties: [{
                    name: 'data-timezone',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownTimezone],
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: UserState.activeShop?.timezone || 'Europe/Paris',
                    list: getTimezoneOptions(),
                    filter: true,
                    optionGroupLabel: 'label',
                    optionGroupChildren: 'items',
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownDateVariable',
                  selector: '{SELECTOR}',
                  parentFieldId: 'countdownDateType',
                  isVisible: (items: any[], parentFieldId: string) => {
                    const property = items.find((item) => item.fieldId === parentFieldId);
                    if (property && Array.isArray(property.properties) && property.properties.length) {
                      return property.properties[0].value === 'variable';
                    }
                    return false;
                  },
                  properties: [{
                    name: 'data-date-variable',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownDateVariable],
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: 'voucher.date_end',
                    list: getCountdownDateVariableOptions(),
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownShowDays',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-show-days',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownShowDays],
                  }],
                  componentName: ComponentNameEnum.CHECKBOX,
                  options: {
                    defaultValue: true,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownShowHours',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-show-hours',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownShowHours],
                  }],
                  componentName: ComponentNameEnum.CHECKBOX,
                  options: {
                    defaultValue: true,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownShowMinutes',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-show-minutes',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownShowMinutes],
                  }],
                  componentName: ComponentNameEnum.CHECKBOX,
                  options: {
                    defaultValue: true,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownShowSeconds',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-show-seconds',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownShowSeconds],
                  }],
                  componentName: ComponentNameEnum.CHECKBOX,
                  options: {
                    defaultValue: true,
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownLabels',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.countdownLabelDays',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-days',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownLabel],
                  }],
                  componentName: ComponentNameEnum.INPUT_VARIABLES,
                  options: {
                    defaultValue: 'JOURS',
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownLabelHours',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-hours',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownLabel],
                  }],
                  componentName: ComponentNameEnum.INPUT_VARIABLES,
                  options: {
                    defaultValue: 'HEURES',
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownLabelMinutes',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-minutes',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownLabel],
                  }],
                  componentName: ComponentNameEnum.INPUT_VARIABLES,
                  options: {
                    defaultValue: 'MIN',
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownLabelSeconds',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-seconds',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownLabel],
                  }],
                  componentName: ComponentNameEnum.INPUT_VARIABLES,
                  options: {
                    defaultValue: 'SEC',
                  },
                  displayToggle: false,
                },
              ],
            },
          ],
        },
      },
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.widgetStyles',
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownDigitStyles',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.backgroundColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-bg-color',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getColorFieldConfig('#ffffff', false, false),
                },
                {
                  label: 'templateBuilder.fields.countdownDigitBackground',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-digit-bg-color',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getColorFieldConfig('#1a1a1a', false, false),
                },
                {
                  label: 'templateBuilder.fields.countdownDigitColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-digit-color',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getColorFieldConfig('#ffffff', false, false),
                },
                {
                  label: 'templateBuilder.fields.countdownDigitSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-digit-size',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 16, 120),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 16, 120).options,
                    defaultValue: 48,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownDigitFontWeight',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-digit-font-weight',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: '700',
                    list: [
                      { value: '400', label: 'Normal' },
                      { value: '500', label: 'Medium' },
                      { value: '600', label: 'Semi-bold' },
                      { value: '700', label: 'Bold' },
                      { value: '800', label: 'Extra-bold' },
                    ],
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownDigitBorderRadius',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-digit-border-radius',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50).options,
                    defaultValue: 8,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownDigitPadding',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-digit-padding',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50).options,
                    defaultValue: 15,
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownLabelStyles',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.countdownLabelColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-color',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getColorFieldConfig('#000000', false, false),
                },
                {
                  label: 'templateBuilder.fields.countdownLabelSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-size',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 8, 48),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 8, 48).options,
                    defaultValue: 13,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownLabelFontWeight',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-label-font-weight',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: '400',
                    list: [
                      { value: '400', label: 'Normal' },
                      { value: '500', label: 'Medium' },
                      { value: '600', label: 'Semi-bold' },
                      { value: '700', label: 'Bold' },
                    ],
                  },
                  displayToggle: false,
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownSeparatorStyles',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.countdownSeparatorType',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-separator-type',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: 'colon',
                    list: [
                      { value: 'none', label: 'templateBuilder.fields.countdownSeparatorNone' },
                      { value: 'colon', label: 'templateBuilder.fields.countdownSeparatorColon' },
                      { value: 'dot', label: 'templateBuilder.fields.countdownSeparatorDot' },
                      { value: 'dash', label: 'templateBuilder.fields.countdownSeparatorDash' },
                      { value: 'pipe', label: 'templateBuilder.fields.countdownSeparatorPipe' },
                    ],
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownSeparatorColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-separator-color',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getColorFieldConfig('#000000', false, false),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownLayoutStyles',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.countdownLayout',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-layout',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  componentName: ComponentNameEnum.DROPDOWN,
                  options: {
                    defaultValue: 'horizontal',
                    list: [
                      { value: 'horizontal', label: 'templateBuilder.fields.countdownLayoutHorizontal' },
                      { value: 'horizontal-compact', label: 'templateBuilder.fields.countdownLayoutHorizontalCompact' },
                      { value: 'boxes', label: 'templateBuilder.fields.countdownLayoutBoxes' },
                      { value: 'circles', label: 'templateBuilder.fields.countdownLayoutCircles' },
                      { value: 'minimal', label: 'templateBuilder.fields.countdownLayoutMinimal' },
                    ],
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.countdownGap',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-gap',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50).options,
                    defaultValue: 10,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownWidth',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-width',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 10, 200, 800),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 10, 200, 800).options,
                    defaultValue: 500,
                  },
                },
                {
                  label: 'templateBuilder.fields.countdownHeight',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-height',
                    getter: getAttributeByName,
                    setters: [setEmailCountdownStyle],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 10, 50, 300),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 10, 50, 300).options,
                    defaultValue: 100,
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.countdownWidgetStyles',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.alignment',
                  selector: '{SELECTOR} > tbody > tr > th',
                  properties: [{ name: 'text-align', getter: getCssPropertyByName, setters: [setCssProperty] }],
                  ...getElementAlignFieldConfig('text-align', AlignValueEnum.CENTER),
                },
              ],
            },
            getWidgetCommonStylesGroup('{SELECTOR} > tbody > tr > th'),
          ],
        },
      },
      getConditionsAndRepetitionGroup('body #spm_body #{ID}'),
    ],
  },
};

export default emailWidgetCountdownConfig;
